import {
  Barcode,
  Bell,
  Confetti,
  Database,
  LinkSimple,
  ShoppingBag,
  Storefront,
  UserCircle,
} from '@phosphor-icons/react';
import { isValidElement, type ReactNode } from 'react';
import type { IconProps } from '@phosphor-icons/react';

export const notificationIcons = {
  order: 'order',
  catalog: 'catalog',
  scm: 'scm',
  crm: 'crm',
  vendor: 'vendor',
  dataStudio: 'data-studio',
  cheers: 'cheers',
};

/*
 * Applied as CSS (`color` and `background` on the badge), never as an SVG
 * attribute, so the token variables resolve.
 */
export const iconColors = {
  yellow: {
    iconColor: 'var(--sk-warning-fg)',
    backgroundColor: 'var(--sk-warning-bg)',
  },
  red: {
    iconColor: 'var(--sk-danger-fg)',
    backgroundColor: 'var(--sk-danger-bg)',
  },
  green: {
    iconColor: 'var(--sk-success-fg)',
    backgroundColor: 'var(--sk-success-bg)',
  },
  default: {
    iconColor: 'var(--sk-fg)',
    backgroundColor: 'var(--sk-rule)',
  },
  /* Over the coloured success, error and warning toasts. */
  notificationIconColor: {
    iconColor: 'var(--sk-surface)',
    backgroundColor: 'color-mix(in srgb, var(--sk-surface) 10%, transparent)',
  },
};

type IconColors = { iconColor: string; backgroundColor: string };

type NotificationRendererProps = {
  defaultNotification?: boolean;
  message: ReactNode;
  iconType?: unknown;
  colors?: IconColors;
};

export function NotificationRenderer({
  defaultNotification,
  message,
  iconType,
  colors = iconColors.notificationIconColor,
}: NotificationRendererProps) {
  const { iconColor, backgroundColor } = defaultNotification
    ? iconColors.default
    : colors;

  return (
    <div className="sk:flex sk:w-full sk:items-center">
      <div
        className="sk:flex sk:min-h-10 sk:min-w-10 sk:items-center sk:justify-center sk:rounded-full"
        style={{ color: iconColor, background: backgroundColor }}
      >
        <Icon iconType={iconType} color="currentColor" />
      </div>
      <p className="sk:ml-2 sk:mr-3.5 sk:w-[265px] sk:overflow-hidden sk:pr-1 sk:text-ellipsis sk:line-clamp-3">
        {message}
      </p>
    </div>
  );
}

export function Icon({
  iconType,
  color,
  ...rest
}: { iconType?: unknown } & IconProps) {
  if (isValidElement(iconType)) return iconType;

  switch (iconType) {
    case notificationIcons.catalog:
      return <Barcode color={color} size={24} {...rest} />;

    case notificationIcons.crm:
      return <UserCircle color={color} size={24} {...rest} />;

    case notificationIcons.dataStudio:
      return <Database color={color} size={24} {...rest} />;

    case notificationIcons.order:
      return <ShoppingBag color={color} size={24} {...rest} />;

    case notificationIcons.scm:
      return <LinkSimple color={color} size={24} {...rest} />;

    case notificationIcons.vendor:
      return <Storefront color={color} size={24} {...rest} />;

    case notificationIcons.cheers:
      return <Confetti color={color} size={24} {...rest} />;

    default:
      return <Bell color={color} size={24} {...rest} />;
  }
}
